/**
 * Утилиты для работы с ролями пользователей
 */

export type UserRole = 'SUPER_ADMIN' | 'OWNER' | 'ADMIN' | 'RECRUITER';

/**
 * Проверяет, является ли пользователь суперадмином
 */
export const isSuperAdmin = (role: UserRole): boolean => {
  return role === 'SUPER_ADMIN';
};

/**
 * Проверяет, является ли пользователь админом или суперадмином
 */
export const isAdminOrAbove = (role: UserRole): boolean => {
  return role === 'SUPER_ADMIN' || role === 'OWNER' || role === 'ADMIN';
};

/**
 * Проверяет, является ли пользователь рекрутером или выше (рекрутер, админ, суперадмин)
 */
export const isRecruiterOrAbove = (role: UserRole): boolean => {
  return role === 'SUPER_ADMIN' || role === 'OWNER' || role === 'ADMIN' || role === 'RECRUITER';
};

/**
 * Проверяет, может ли пользователь создавать пользователей только в своей компании
 * (админ и рекрутер)
 */
export const isLimitedToOwnCompany = (role: UserRole): boolean => {
  return role === 'OWNER' || role === 'ADMIN' || role === 'RECRUITER';
};

/**
 * Проверяет, может ли пользователь управлять всеми компаниями
 * (только суперадмин)
 */
export const canManageAllCompanies = (role: UserRole): boolean => {
  return role === 'SUPER_ADMIN';
};

/**
 * Проверяет, может ли пользователь создавать пользователей
 * (все роли)
 */
export const canCreateUsers = (role: UserRole): boolean => {
  return isRecruiterOrAbove(role);
};

/**
 * Проверяет, может ли пользователь редактировать пользователей
 * (все роли)
 */
export const canEditUsers = (role: UserRole): boolean => {
  return isRecruiterOrAbove(role);
};

/**
 * Проверяет, может ли пользователь удалять пользователей
 * (только админ и суперадмин)
 */
export const canDeleteUsers = (role: UserRole): boolean => {
  return isAdminOrAbove(role);
};

/**
 * Получает список ролей, которые может назначать пользователь
 */
export const getAssignableRoles = (currentUserRole: UserRole): UserRole[] => {
  switch (currentUserRole) {
    case 'SUPER_ADMIN':
      return ['SUPER_ADMIN', 'OWNER', 'ADMIN', 'RECRUITER'];
    case 'OWNER':
      return ['ADMIN', 'RECRUITER'];
    case 'ADMIN':
      return ['RECRUITER'];
    case 'RECRUITER':
      return []; // Рекрутер не может назначать роли
    default:
      return [];
  }
};

/**
 * Проверяет, может ли пользователь назначать указанную роль
 */
export const canAssignRole = (currentUserRole: UserRole, targetRole: UserRole): boolean => {
  const assignableRoles = getAssignableRoles(currentUserRole);
  return assignableRoles.includes(targetRole);
};
